import React from 'react';
import {
  MAX_TRIP_PHOTOS,
  MAX_TRIP_PHOTO_BATCH,
  TripMediaView,
  getTripMedia,
  presignTripMedia,
} from '../../../../services/trips/api/tripsApi';
import { reencodeImage, uploadToPresignedPost } from '../../../../services/media/upload';
import PhotoLightbox from '../../feed/PhotoLightbox/PhotoLightbox';
import './TripPhotos.css';

interface TripPhotosProps {
  /** Trip UUID (not the `TRIP#` key). */
  tripId: string;
  /** Owner/collaborator — anyone else sees the gallery read-only. */
  canEdit: boolean;
}

/**
 * A trip's photo gallery, and the upload widget that fills it (T2.6d).
 *
 * The upload is three hops and the ORDER of the first two is the privacy
 * control, not an optimisation:
 *
 *   1. re-encode in a canvas — EXIF, and with it GPS, never leaves the device
 *   2. presign — one request mints every slot the batch needs
 *   3. POST straight to S3 — image bytes never touch our API
 *
 * The pipeline (validate → strip → moderate → thumbnail) then runs
 * asynchronously, so a just-uploaded photo comes back `pending` with no URL.
 * That is why this polls: the alternative is an <img> pointed at nothing.
 * Polling is bounded — a photo the pipeline rejects (HEIC that the browser
 * couldn't decode, or a moderation block) never becomes `live`, and an
 * unbounded poll would hammer the API forever waiting for it.
 */
const POLL_INTERVAL_MS = 4000;
const MAX_POLLS = 15; // ~1 minute, comfortably longer than a normal round trip

const TripPhotos: React.FC<TripPhotosProps> = ({ tripId, canEdit }) => {
  const [photos, setPhotos] = React.useState<TripMediaView[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isUploading, setIsUploading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = React.useState<number | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const load = React.useCallback(async (): Promise<TripMediaView[]> => {
    const media = await getTripMedia(tripId);
    setPhotos(media);
    return media;
  }, [tripId]);

  React.useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    load()
      .catch(() => { if (!cancelled) setError('Could not load photos.'); })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [load]);

  const live = photos.filter((photo) => photo.status === 'live');
  const pendingCount = photos.filter((photo) => photo.status === 'pending').length;
  const hasPending = pendingCount > 0;

  // Re-poll only while something is still processing, and only for a while.
  // The dependency is the BOOLEAN, not `photos`: every poll replaces the photos
  // array, and depending on that would tear the interval down and rebuild it
  // each tick — resetting the counter and turning "bounded" into "forever".
  React.useEffect(() => {
    if (!hasPending) return undefined;

    let polls = 0;
    const timer = setInterval(() => {
      polls += 1;
      if (polls > MAX_POLLS) {
        clearInterval(timer);
        return;
      }
      void load().catch(() => { /* transient — the next tick retries */ });
    }, POLL_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [hasPending, load]);

  // The lightbox wants the ORIGINAL: a 256px thumbnail blown up to fill the
  // screen is the bug this falls back from, not the intent.
  const fullSizeUrls = live
    .map((photo) => photo.url || photo.thumbUrl)
    .filter((url): url is string => Boolean(url));
  const remaining = MAX_TRIP_PHOTOS - photos.length;

  const handleFiles = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    // Let the same file be picked twice in a row — without this the input keeps
    // its value and the second pick fires no change event.
    event.target.value = '';
    if (files.length === 0) return;

    if (remaining <= 0) {
      setError(`This trip already has the maximum of ${MAX_TRIP_PHOTOS} photos.`);
      return;
    }

    const batch = files.slice(0, Math.min(MAX_TRIP_PHOTO_BATCH, remaining));
    setIsUploading(true);
    setError(null);

    try {
      const encoded = await Promise.all(batch.map((file) => reencodeImage(file)));
      const targets = await presignTripMedia(tripId, encoded.length);

      const results = await Promise.all(encoded.map(async (item, index) => {
        const target = targets[index];
        if (!target) return false;
        try {
          await uploadToPresignedPost(target.upload, item.blob, item.contentType);
          return true;
        } catch {
          return false;
        }
      }));

      // One message, chosen once — an upload failure matters more than a
      // truncated batch, and setting both would just overwrite the first.
      const failed = results.filter((ok) => !ok).length;
      if (failed === results.length) {
        setError('Upload failed. Please try again.');
      } else if (failed > 0) {
        setError(`${failed} of ${results.length} photos failed to upload.`);
      } else if (files.length > batch.length) {
        setError(
          `Added ${batch.length} of ${files.length} — up to ${MAX_TRIP_PHOTO_BATCH} at a time, `
          + `${MAX_TRIP_PHOTOS} per trip.`,
        );
      }

      // Reload regardless: the successful ones are already `pending` server-side
      // and the poll above takes over from here.
      await load();
    } catch (uploadError: any) {
      setError(uploadError?.status === 403
        ? 'Only the trip owner and collaborators can add photos.'
        : 'Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <section className="v2-trip-photos" aria-labelledby="trip-photos-heading">
      <header className="v2-trip-photos__header">
        <div>
          <p>Trip album</p>
          <h2 id="trip-photos-heading">
            Photos{live.length > 0 ? ` · ${live.length}` : ''}
          </h2>
        </div>
        {canEdit && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFiles}
              className="v2-trip-photos__input"
              aria-hidden
              tabIndex={-1}
            />
            <button
              type="button"
              className="v2-trip-photos__add"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading || remaining <= 0}
            >
              {isUploading ? 'Uploading…' : 'Add photos'}
            </button>
          </>
        )}
      </header>

      {error && <p className="v2-trip-photos__error" role="alert">{error}</p>}

      {isLoading ? (
        <p className="v2-trip-photos__empty">Loading photos…</p>
      ) : live.length === 0 && pendingCount === 0 ? (
        <p className="v2-trip-photos__empty">
          {canEdit
            ? 'No photos yet — add a few from the trip.'
            : 'No photos on this trip yet.'}
        </p>
      ) : (
        <div className="v2-trip-photos__grid">
          {live.map((photo, index) => (
            <button
              type="button"
              key={photo.mediaId}
              className="v2-trip-photos__tile"
              onClick={() => setLightboxIndex(index)}
              aria-label={`Open photo ${index + 1} of ${live.length}`}
            >
              <img src={photo.thumbUrl || photo.url} alt="" loading="lazy" />
            </button>
          ))}
          {Array.from({ length: pendingCount }, (_, index) => (
            <div
              key={`pending-${index}`}
              className="v2-trip-photos__tile v2-trip-photos__tile--pending"
            >
              <span>Processing…</span>
            </div>
          ))}
        </div>
      )}

      <PhotoLightbox
        images={fullSizeUrls}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onIndexChange={setLightboxIndex}
        label="Trip photo"
      />
    </section>
  );
};

export default TripPhotos;
